import React from 'react';
import { Edit2, Trash2, Building2 } from 'lucide-react';

export default function DepartmentList({ departments, positions, orgUnits = [], onEdit, onDelete }) {
  const getPositionCount = (deptId) => {
    return positions.filter(p => p.departamentoId === deptId).length;
  };

  const getUnitName = (unitId) => {
    if (!unitId) return null;
    const unit = orgUnits.find(u => u.id === unitId);
    return unit ? { nombre: unit.nombre, color: unit.color } : null;
  };

  if (departments.length === 0) {
    return (
      <div className="py-16 text-center">
        <Building2 className="w-12 h-12 text-gray-200 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-500 mb-1">No hay departamentos registrados</p>
        <p className="text-xs text-gray-400">Crea el primer departamento para comenzar</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-blue-600 text-white">
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Departamento</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Unidad</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Responsable</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Ubicación</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Cargos</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider">Acciones</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {departments.map(dept => (
            <tr key={dept.id} className="hover:bg-gray-50 transition-colors">
              {/* Nombre */}
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-1 h-10 rounded flex-shrink-0"
                    style={{ backgroundColor: dept.color }}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{dept.nombre}</div>
                    {dept.descripcion && (
                      <div className="text-xs text-gray-500 truncate max-w-xs mt-0.5">
                        {dept.descripcion}
                      </div>
                    )}
                  </div>
                </div>
              </td>

              {/* Unidad */}
              <td className="px-6 py-4">
                {(() => {
                  const unit = getUnitName(dept.organizational_unit_id);
                  return unit ? (
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: unit.color }} />
                      <span className="text-sm text-gray-700">{unit.nombre}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  );
                })()}
              </td>

              {/* Responsable */}
              <td className="px-6 py-4">
                <span className="text-sm text-gray-700">{dept.responsable || '—'}</span>
              </td>

              {/* Ubicación */}
              <td className="px-6 py-4">
                <span className="text-sm text-gray-700">{dept.ubicacion || '—'}</span>
              </td>

              {/* Cargos count */}
              <td className="px-6 py-4 text-center">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                  {getPositionCount(dept.id)}
                </span>
              </td>

              {/* Acciones */}
              <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => onEdit(dept)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      const count = getPositionCount(dept.id);
                      if (count > 0) {
                        alert(`No puede eliminar "${dept.nombre}" porque tiene ${count} cargo(s) asignado(s).`);
                        return;
                      }
                      if (window.confirm(`¿Eliminar departamento "${dept.nombre}"?\n\nEsta acción no se puede deshacer.`)) {
                        onDelete(dept.id);
                      }
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Eliminar
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50 border-t border-gray-200">
          <tr>
            <td colSpan="6" className="px-6 py-3">
              <p className="text-xs text-gray-500">
                <span className="font-semibold text-gray-700">{departments.length}</span>{' '}
                departamento{departments.length !== 1 ? 's' : ''}
              </p>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
